import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AccountAutoComplete,
  OptionSetsDropdown,
  StorageLocationAutoComplete,
} from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SEX_EMBRYO } from '@/constants';
import { FormField } from '@/pages/embryo-details/components/FormField';
import {
  FreezeEntryDto,
  FreezeEntryInput,
  useEditFreezeEntriesMutation,
} from '@/services/embryo-service';
import { useToast } from '@/services/toast';

const sexOptions = Object.entries(SEX_EMBRYO).map(([label, value]) => ({
  label,
  value,
}));

const statusOptions = [
  { label: 'Active', value: 0 },
  { label: 'Inactive', value: 1 },
];

const formFields = [
  {
    label: 'Status',
    name: 'stateCode',
    component: Dropdown,
    componentProps: { options: statusOptions },
  },
  { label: 'Straw', name: 'straw', component: InputNumber },
  { label: 'Stage', name: 'stage', component: InputNumber },
  { label: 'Grade', name: 'grade', component: InputNumber },
  { label: 'Cane', name: 'cane', component: InputText },
  {
    label: 'Sex',
    name: 'sex',
    component: Dropdown,
    componentProps: { options: sexOptions },
  },
  { label: 'Freeze Tech', name: 'freezeTech', component: InputText },
  {
    label: 'Freeze Method',
    name: 'freezeMethodId',
    component: OptionSetsDropdown,
    componentProps: { optionSetsName: 'Freezing Methods' },
  },
  { label: 'Freezer', name: 'freezer', component: InputText },
  {
    label: 'Storage Location',
    name: 'storageLocationId',
    component: StorageLocationAutoComplete,
  },
  { label: 'Owner', name: 'ownerId', component: AccountAutoComplete },
  { label: 'TT', name: 'tt', component: Checkbox },
  { label: 'Low Quality', name: 'lowQuality', component: Checkbox },
  { label: 'Cracked Zona', name: 'crackedZona', component: Checkbox },
  { label: 'Discard', name: 'discard', component: Checkbox },
  { label: 'Freeze Time', name: 'freezeTime', component: Calendar },
  { label: 'Embryo Created', name: 'embryoCreated', component: Calendar },
];

const freezeEntryEditSchema = z.object({
  stateCode: z.number().nullable().optional(),
  cane: z.string().nullable().optional(),
  freezeTech: z.string().nullable().optional(),
  freezer: z.string().nullable().optional(),
  straw: z.number().nullable().optional(),
  grade: z.number().nullable().optional(),
  sex: z.number().nullable().optional(),
  storageLocationId: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  freezeMethodId: z.string().nullable().optional(),
  stage: z.number().nullable().optional(),
  tt: z.boolean().nullable().optional(),
  lowQuality: z.boolean().nullable().optional(),
  crackedZona: z.boolean().nullable().optional(),
  discard: z.boolean().nullable().optional(),
  freezeTime: z.any().optional(),
  embryoCreated: z.any().optional(),
});

type FreezeEntryEditFormValues = z.infer<typeof freezeEntryEditSchema>;

type EditFreezeEntriesDialogProps = {
  visible: boolean;
  selectedRows: FreezeEntryDto[];
  onHide: () => void;
  onSuccess?: () => void;
  showEmbryoUpdateNote?: boolean;
};

export const EditFreezeEntriesDialog = ({
  visible,
  selectedRows,
  onHide,
  onSuccess,
  showEmbryoUpdateNote = false,
}: EditFreezeEntriesDialogProps) => {
  const toast = useToast();
  const { mutate: editFreezeEntries, isPending } =
    useEditFreezeEntriesMutation();

  const { control, handleSubmit, reset } = useForm<FreezeEntryEditFormValues>({
    resolver: zodResolver(freezeEntryEditSchema),
    defaultValues: {},
  });

  const onSubmit = useCallback(
    (formValues: FreezeEntryEditFormValues) => {
      const changedFields = Object.fromEntries(
        Object.entries(formValues).filter(
          ([, value]) => value !== null && value !== undefined && value !== '',
        ),
      );

      if (Object.keys(changedFields).length === 0) {
        toast.show({
          severity: 'warn',
          summary: 'Warning',
          detail: 'No changes to apply',
          life: 3000,
        });
        return;
      }

      const payload: FreezeEntryInput[] = selectedRows.map((row) => ({
        ...row,
        ...changedFields,
        id: row.id,
      }));

      const hasAssociatedEmbryo = selectedRows.some((row) => row.embryoId);

      editFreezeEntries(payload, {
        onSuccess: () => {
          const detail = hasAssociatedEmbryo
            ? `${selectedRows.length} freeze entry/entries and associated embryo(s) updated successfully`
            : `${selectedRows.length} freeze entry/entries updated successfully`;
          toast.show({
            severity: 'success',
            summary: 'Success',
            detail,
            life: 3000,
          });
          reset({});
          onSuccess?.();
          onHide();
        },
        onError: (error: Error) => {
          toast.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update freeze entries',
            life: 5000,
          });
        },
      });
    },
    [selectedRows, editFreezeEntries, toast, reset, onSuccess, onHide],
  );

  const handleCancel = useCallback(() => {
    reset({});
    onHide();
  }, [reset, onHide]);

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
    >
      <DialogContent className="md:max-w-[50vw] 2xl:max-w-[70vw]">
        <DialogHeader>
          <DialogTitle>
            {`Edit ${selectedRows.length} Freeze Entry/Entries`}
          </DialogTitle>
        </DialogHeader>
        <p className="mb-4 text-sm text-gray-500">
          Only filled fields will be applied to all selected rows.
          {showEmbryoUpdateNote &&
            ' Changes will also update the associated live embryo record if one exists.'}
        </p>
        <div className="grid grid-cols-3 gap-x-6 gap-y-1">
          {formFields.map((field) => (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              control={control}
              component={field.component}
              componentProps={field.componentProps}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending}>
            {isPending && (
              <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
